/**
 * 채팅 전송 오케스트레이션 — 결정 #7/#12/#14를 한데 묶는 곳.
 *
 * 흐름:
 *  1) 사용자 메시지를 즉시 저장(결정 #9: 메시지 단위 행).
 *  2) 필요한 엔티티(캐릭터·페르소나·프리셋·로어북·히스토리)를 로드.
 *  3) core의 buildPrompt로 요청 조립(결정 #12).
 *  4) 스트리밍이면 델타를 onDelta 콜백으로 흘려보냄 — UI가 ref버퍼+rAF로 배치
 *     (결정 #14). 이 모듈은 setState를 호출하지 않는다.
 *  5) 완료 시 어시스턴트 메시지를 "1회" 커밋(결정 #14).
 *
 * 저장소/게이트웨이는 core 인터페이스로만 다룬다(결정 #10).
 */
import {
  applyRegexScripts,
  buildLorebookScanText,
  buildPrompt,
  contextUsage,
  createMessage,
  estimateRequestTokens,
  estimateTokenCost,
  heuristicTokenCount,
  makeSeededRandom,
  parseTurn,
  providerSupportsCapability,
  resolvePricing,
  ROLEPLAY_MODULE_ID,
  type Character,
  type Chat,
  type CompletionRequest,
  type CompletionUsage,
  type ContextUsage,
  type GameModule,
  type Lorebook,
  type Message,
  type Persona,
  type Preset,
  type ProviderProfile,
  type SamplingParams,
  type Script,
  type TokenCounter,
  type ActivationByPosition,
  type RoqeetRunContext,
  type TurnDiagnostic,
} from "@roqeet/core";
import { storage } from "./storage.js";
import { aiGateway } from "./ai.js";
import { gameModules } from "./game-modules.js";
import { runScriptsForHook } from "./script-actions.js";
import { getExactTokenCounter } from "../platform/tokenizer/index.js";
import { scanHost } from "./lorebook.js";
import {
  buildModuleTurnDebug,
  MODULE_STATE_AFTER,
  MODULE_STATE_BEFORE,
  MODULE_TURN_DEBUG,
  MODULE_TURN_DIAGNOSTICS,
  MODULE_TURN_DIFF,
  MODULE_TURN_PARSED,
  type ModuleTurnDebug,
} from "./game-turn-debug.js";

export interface SendCallbacks {
  /** 스트리밍 델타(텍스트 조각). UI가 rAF로 배치 렌더(결정 #14). */
  onDelta?: (deltaText: string) => void;
  /** 추론(thinking) 델타. 별도 표시용. */
  onReasoning?: (deltaText: string) => void;
  /** 어시스턴트 응답이 시작될 때(플레이스홀더 생성 시점). */
  onStart?: () => void;
}

export interface SendResult {
  userMessage: Message;
  assistantMessage: Message;
}

/** 활성 프로바이더 프로필을 해석한다. 없으면 명확한 에러. */
async function resolveProfile(chat: Chat): Promise<ProviderProfile> {
  const settings = await storage.getSettings();
  const profiles = await storage.providerProfiles.list();
  const enabled = profiles.filter((p) => p.enabled && providerSupportsCapability(p, "chat"));
  // 우선순위: 대화별 override(extensions) → 설정 기본값 → 활성 프로필 첫 번째.
  const ext = chat.extensions as { providerProfileId?: string } | undefined;
  const id = ext?.providerProfileId ?? settings?.defaultProviderProfileId;
  const chosen = (id && enabled.find((p) => p.id === id)) || enabled[0];
  if (!chosen) {
    throw new Error(
      "사용 가능한 AI 프로바이더가 없습니다. 설정에서 프로바이더와 API 키를 먼저 추가하세요.",
    );
  }
  return chosen;
}

/** 프리셋을 해석한다(채팅 지정 → 설정 기본 → 첫 프리셋). */
async function resolvePreset(chat: Chat): Promise<Preset> {
  const presets = await storage.presets.list();
  const settings = await storage.getSettings();
  const id = chat.presetId ?? settings?.defaultPresetId;
  const chosen = (id && presets.find((p) => p.id === id)) || presets[0];
  if (!chosen) throw new Error("프리셋이 없습니다. 기본 프리셋 시드가 누락되었습니다.");
  return chosen;
}

/** 1:1 채팅에서 응답할 캐릭터를 찾는다(결정 #22 참가자 모델). */
async function resolveCharacter(chat: Chat): Promise<Character> {
  const seat = chat.participants.find((p) => p.type === "character" && !p.muted);
  if (!seat?.characterId) throw new Error("이 대화에 캐릭터 참가자가 없습니다.");
  const character = await storage.characters.get(seat.characterId);
  if (!character) throw new Error("캐릭터를 찾을 수 없습니다.");
  return character;
}

/** 사용자 페르소나를 해석한다(참가자 → 기본 페르소나). */
async function resolvePersona(chat: Chat): Promise<Persona | undefined> {
  const seat = chat.participants.find((p) => p.type === "user");
  if (seat?.personaId) {
    const p = await storage.personas.get(seat.personaId);
    if (p) return p;
  }
  const personas = await storage.personas.list();
  const settings = await storage.getSettings();
  return personas.find((p) => p.id === settings?.defaultPersonaId) ?? personas[0];
}

/** 캐릭터 임베드 + 채팅 레벨 로어북을 모은다(결정 #12). */
async function resolveLorebooks(chat: Chat, character: Character): Promise<Lorebook[]> {
  const ids = new Set<string>();
  if (character.lorebookId) ids.add(character.lorebookId);
  for (const id of chat.lorebookIds) ids.add(id);
  if (ids.size === 0) return [];
  const books = await storage.lorebooks.getMany([...ids]);
  return books.filter((b): b is Lorebook => !!b);
}

/** 캐릭터에 연결된 활성 스크립트 전체(정규식 + JS)를 로드한다(결정 #17). */
async function resolveAllScripts(character: Character): Promise<Script[]> {
  if (!character.scriptIds.length) return [];
  const scripts = await storage.scripts.getMany(character.scriptIds);
  return scripts.filter((s): s is Script => !!s && s.enabled);
}

/**
 * 세션의 게임 모듈을 해석한다(결정 #25). moduleId가 없으면 롤플레잉(모듈 #1).
 * 등록되지 않은 모듈이면 undefined → 자유대화 경로로 폴백(결정 #28 가산성).
 */
function resolveModule(chat: Chat): GameModule | undefined {
  return gameModules.get(chat.moduleId ?? ROLEPLAY_MODULE_ID);
}

/**
 * 모듈의 턴 프로토콜 지시문을 현재 상태로 만든다(결정 #26). 자유대화 모듈(훅 없음)은
 * undefined를 반환해 buildPrompt가 기존 경로를 쓴다.
 */
function moduleInstructionsFor(module: GameModule | undefined, chat: Chat): string | undefined {
  if (!module?.protocolInstructions) return undefined;
  return module.protocolInstructions(chat.moduleState ?? {});
}

/** 모듈 상태 스냅샷을 메시지 extensions에 저장하는 키(결정 #16 재생성 롤백용). */
/**
 * 구조화 게임 턴을 처리한다(결정 #26/#28). 원본 AI 출력을 parseTurn으로 분해하고
 * applyTurn으로 상태를 갱신한다. 화면/저장용 본문은 narration(태그 제거)으로 한다.
 * 자유대화 모듈(applyTurn 없음)은 fullText를 그대로 본문으로 쓰고 상태 변화 없음.
 *
 * @param seed 재현 가능한 주사위용 시드(결정 #4). 같은 턴은 같은 결과.
 */
function processGameTurn(
  module: GameModule | undefined,
  chat: Chat,
  fullText: string,
  seed: number,
): {
  narration: string;
  nextState: Record<string, unknown> | undefined;
  ended: boolean;
  endingLabel?: string;
  diagnostics?: TurnDiagnostic[];
  debug?: ModuleTurnDebug;
} {
  if (!module?.applyTurn) {
    return { narration: fullText, nextState: undefined, ended: false };
  }
  const prevState = cloneRecord(chat.moduleState ?? {});
  const parsed = parseTurn(fullText);
  const outcome = module.applyTurn({
    state: prevState,
    parsed,
    random: makeSeededRandom(seed),
  });
  // narration이 비면(태그만 출력된 경우) 원문을 폴백해 빈 말풍선을 막는다(결정 #26).
  const narration = parsed.narration.trim() || fullText;
  const ended = outcome.ended ?? false;
  // 종료 신호를 moduleState에 정규화해 영속한다(결정 #28). 각 모듈이 ended를 제각각
  // 도출(hp<=0·escaped·solved …)하더라도, 저장된 상태의 ended/endingLabel을 보면
  // UI가 일관되게 게임 종료를 감지·표시할 수 있다(ChatRoomView 재시작 배너).
  const nextState: Record<string, unknown> = ended
    ? {
        ...outcome.state,
        ended: true,
        ...(outcome.endingLabel ? { endingLabel: outcome.endingLabel } : {}),
      }
    : outcome.state;
  const result: ReturnType<typeof processGameTurn> = {
    narration,
    nextState,
    ended,
    debug: buildModuleTurnDebug({
      before: prevState,
      after: nextState,
      parsed,
      diagnostics: outcome.diagnostics,
      ended,
      endingLabel: outcome.endingLabel,
    }),
  };
  if (outcome.endingLabel) result.endingLabel = outcome.endingLabel;
  const diagnostics = [...parsed.diagnostics, ...(outcome.diagnostics ?? [])];
  if (diagnostics.length) result.diagnostics = diagnostics;
  return result;
}

function cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
  return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
}

/** 다음 order 값(현재 최대 + 1). 메시지 단위 저장이라 저렴(결정 #9). */
function nextOrder(messages: Message[]): number {
  return messages.length ? Math.max(...messages.map((m) => m.order)) + 1 : 0;
}

/** 한 페이지에 읽을 메시지 수(커서 페이징, 결정 #9). */
const HISTORY_PAGE_SIZE = 100;
/** 토큰 예산을 못 구할 때의 보수적 기본 컨텍스트 크기. */
const DEFAULT_CONTEXT_SIZE = 8192;
/** 적어도 이만큼은 항상 로드(짧은 대화/예산 무관 안전 하한). */
const MIN_RECENT_MESSAGES = 60;
/** per-turn 오버헤드 근사(build-prompt와 동일 — 역방향 누적 시 일관성). */
const PER_TURN_OVERHEAD = 4;

/**
 * 컨텍스트 조립에 필요한 "최근 메시지"만 역방향(최신→과거)으로 로드한다(결정 #9).
 *
 * 왜 안전한가: buildPrompt는 히스토리를 끝(최신)에서부터 토큰 예산만큼 유지하고
 * 오래된 앞부분을 버린다(트림). 또 로어북 스캔도 slice(-maxDepth)로 최근만 본다.
 * 따라서 "예산을 채우고도 남을 만큼"의 최근 메시지만 주면 트림 결과가 전체 로드와
 * **동일**하다. 여기서는 contextSize의 2배 토큰( + 최소 메시지 수)까지 모아 잉여를
 * 확보한다. 매우 긴 대화에서 전체를 6s+ 메모리로 끌어오지 않는 게 핵심 이득이다.
 *
 * 토큰 카운터는 buildPrompt와 같은 것을 주입해 측정 일관성을 맞춘다(없으면 휴리스틱).
 */
export async function readRecentMessages(
  chatId: string,
  contextSize: number = DEFAULT_CONTEXT_SIZE,
  counter: TokenCounter = heuristicTokenCount,
): Promise<Message[]> {
  // 트림이 버릴 일이 없도록 예산의 2배를 목표로 모은다(역방향 누적).
  const tokenTarget = Math.max(1, contextSize) * 2;

  const collected: Message[] = [];
  let tokens = 0;
  let beforeOrder: number | undefined; // 첫 페이지는 최신(undefined)부터.

  for (;;) {
    const page = await storage.getMessagePage({
      chatId,
      ...(beforeOrder !== undefined ? { beforeOrder } : {}),
      limit: HISTORY_PAGE_SIZE,
    });
    if (page.length === 0) break;

    // page는 order 오름차순 — 역방향 누적이므로 앞(과거)에 이어 붙인다.
    collected.unshift(...page);
    for (const m of page) {
      tokens += counter(m.variants[m.activeVariant]?.content ?? "") + PER_TURN_OVERHEAD;
    }

    const enoughTokens = tokens >= tokenTarget;
    const enoughCount = collected.length >= MIN_RECENT_MESSAGES;
    const exhausted = page.length < HISTORY_PAGE_SIZE;
    // 토큰·개수 하한을 모두 만족했거나 더 읽을 게 없으면 종료.
    if (exhausted || (enoughTokens && enoughCount)) break;

    beforeOrder = page[0]!.order; // 이 페이지의 가장 오래된 것보다 더 과거로.
  }

  return collected;
}

/** 문자열 → 32bit 시드(djb2). 로어북 확률 게이트 재현성용(결정 #4). */
function lorebookSeed(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return h >>> 0;
}

/**
 * 로어북 활성화를 Web Worker로 오프로딩해 계산한다(결정 #9/#12). 결과를 buildPrompt의
 * `activation`에 주입하면 내부 스캔을 건너뛴다. 로어북이 없으면 undefined(주입 안 함 →
 * buildPrompt가 빈 활성화를 자체 처리).
 *
 * scanText는 buildPrompt 내부와 동일하게 Roqeet 인라인 값을 전개해 만든다.
 * 따라서 주입 결과는 내부 스캔과 등가다(확률 없는 일반 엔트리는 완전 동일).
 */
async function resolveActivation(
  lorebooks: Lorebook[],
  messages: Message[],
  character: Character,
  persona: Persona | undefined,
  chatId: string,
): Promise<ActivationByPosition | undefined> {
  if (lorebooks.length === 0) return undefined;
  const roqeet: RoqeetRunContext = { char: character.name, user: persona?.name ?? "" };
  const scanText = buildLorebookScanText(messages, lorebooks, roqeet);
  const seed = lorebookSeed(`${chatId}:${messages.length}`);
  try {
    return await scanHost.scan(lorebooks, scanText, seed);
  } catch {
    // 오프로딩 실패는 비치명적 — buildPrompt가 내부 스캔으로 처리하도록 undefined.
    return undefined;
  }
}

/**
 * 현재 대화로 프롬프트를 조립해 컨텍스트 사용량을 추정한다(결정 #12). 전송하지
 * 않고 사용량만 계산 — 채팅방 헤더의 "n/k 토큰" 표시용. 실패 시 undefined.
 */
export async function estimateContextUsage(chatId: string): Promise<ContextUsage | undefined> {
  try {
    const chat = await storage.chats.get(chatId);
    if (!chat) return undefined;
    const [character, persona, preset, profile] = await Promise.all([
      resolveCharacter(chat),
      resolvePersona(chat),
      resolvePreset(chat),
      resolveProfile(chat).catch(() => undefined),
    ]);
    const effectivePreset = withProviderSamplingDefaults(preset, profile);
    const lorebooks = await resolveLorebooks(chat, character);
    const moduleInstructions = moduleInstructionsFor(resolveModule(chat), chat);

    // 정확 토크나이저(결정 #12): 대화의 모델로 인코딩을 골라 lazy-load한다. 프로바이더가
    // 없거나 로드 실패면 휴리스틱으로 폴백한다(표시가 깨지지 않게).
    const model = await resolveModelForEstimate(chat);
    const tokenCounter = await getExactTokenCounter(model, effectivePreset.sampling.tokenizer);
    // 최근 메시지만 로드(결정 #9). 트림 결과는 전체 로드와 동일(예산의 2배 확보).
    const messages = await readRecentMessages(chatId, effectivePreset.sampling.contextSize, tokenCounter);
    // 로어북 스캔은 Web Worker로 오프로딩(결정 #9/#12). 결과를 주입하면 buildPrompt가
    // 내부 스캔을 건너뛴다(메인스레드 부하 절감). 결과는 내부 스캔과 등가.
    const activation = await resolveActivation(lorebooks, messages, character, persona, chatId);

    const req = buildPrompt({
      preset: effectivePreset,
      character,
      chat,
      messages,
      model,
      stream: false,
      tokenCounter,
      ...(persona ? { persona } : {}),
      ...(lorebooks.length ? { lorebooks } : {}),
      ...(activation ? { activation } : {}),
      ...(moduleInstructions ? { moduleInstructions } : {}),
    });
    return contextUsage(req, tokenCounter);
  } catch {
    return undefined;
  }
}

/** 사용량 추정용 모델명 해석(프로바이더가 없으면 일반 기본값). 비치명적. */
async function resolveModelForEstimate(chat: Chat): Promise<string> {
  try {
    const profile = await resolveProfile(chat);
    return profile.defaultModel ?? "gpt-5.5";
  } catch {
    return "gpt-5.5";
  }
}

function withProviderSamplingDefaults(preset: Preset, profile: ProviderProfile | undefined): Preset {
  const defaults = profile?.samplingDefaults;
  if (!defaults) return preset;

  const sampling = mergeProviderSamplingDefaults(preset.sampling, defaults);
  return sampling === preset.sampling ? preset : { ...preset, sampling };
}

function mergeProviderSamplingDefaults(
  sampling: SamplingParams,
  defaults: NonNullable<ProviderProfile["samplingDefaults"]>,
): SamplingParams {
  let changed = false;
  const next: SamplingParams = { ...sampling };

  if (
    defaults.reasoningLevel &&
    (sampling.reasoningLevel == null || sampling.reasoningLevel === "auto")
  ) {
    next.reasoningLevel = defaults.reasoningLevel;
    changed = true;
  }
  if (defaults.reasoningBudgetTokens != null && sampling.reasoningBudgetTokens == null) {
    next.reasoningBudgetTokens = defaults.reasoningBudgetTokens;
    changed = true;
  }
  if (defaults.tokenizer && (sampling.tokenizer == null || sampling.tokenizer === "auto")) {
    next.tokenizer = defaults.tokenizer;
    changed = true;
  }

  return changed ? next : sampling;
}

/**
 * 현재 대화 상태로 조립된 프롬프트를 그대로 반환한다(결정 #12 디버깅용). 전송하지
 * 않으며, 채팅 설정의 "프롬프트 미리보기"에서 모델에 실제로 가는 메시지를 확인하는
 * 데 쓴다. 카드/프리셋/로어북 디버깅의 핵심 도구.
 */
export async function previewPrompt(chatId: string): Promise<CompletionRequest> {
  const chat = await storage.chats.get(chatId);
  if (!chat) throw new Error("대화를 찾을 수 없습니다.");
  const [character, persona, preset, profile] = await Promise.all([
    resolveCharacter(chat),
    resolvePersona(chat),
    resolvePreset(chat),
    resolveProfile(chat).catch(() => undefined),
  ]);
  const effectivePreset = withProviderSamplingDefaults(preset, profile);
  const lorebooks = await resolveLorebooks(chat, character);
  const messages = await readRecentMessages(chatId, effectivePreset.sampling.contextSize);
  const model = profile?.defaultModel ?? "preview";
  const moduleInstructions = moduleInstructionsFor(resolveModule(chat), chat);
  const activation = await resolveActivation(lorebooks, messages, character, persona, chatId);
  return buildPrompt({
    preset: effectivePreset,
    character,
    chat,
    messages,
    model: String(model),
    stream: false,
    ...(persona ? { persona } : {}),
    ...(lorebooks.length ? { lorebooks } : {}),
    ...(activation ? { activation } : {}),
    ...(moduleInstructions ? { moduleInstructions } : {}),
  });
}

/**
 * 사용자 텍스트를 보내고 AI 응답을 생성한다. 스트리밍 델타는 콜백으로 흘리고,
 * 완료 시 메시지를 1회 커밋한다(결정 #14).
 */
export async function sendUserMessage(
  chatId: string,
  userText: string,
  callbacks: SendCallbacks = {},
  signal?: AbortSignal,
): Promise<SendResult> {
  const chat = await storage.chats.get(chatId);
  if (!chat) throw new Error("대화를 찾을 수 없습니다.");

  const [character, persona, preset, profile] = await Promise.all([
    resolveCharacter(chat),
    resolvePersona(chat),
    resolvePreset(chat),
    resolveProfile(chat),
  ]);
  const lorebooks = await resolveLorebooks(chat, character);
  const allScripts = await resolveAllScripts(character);
  const regexScripts = allScripts.filter((s) => s.language === "regex");
  // 게임 모듈 해석(결정 #25). 자유대화(롤플레잉)는 훅이 없어 undefined.
  const module = resolveModule(chat);
  // 이번 턴 적용 전 상태 스냅샷(결정 #16 재생성 롤백용).
  const moduleStateBefore = cloneRecord(chat.moduleState ?? {});

  // 컨텍스트 조립에 쓸 모델·토크나이저를 먼저 정한다(히스토리 로드에 같은 카운터를
  // 써서 측정 일관성 확보, 결정 #9/#12).
  const effectivePreset = withProviderSamplingDefaults(preset, profile);
  const model = profile.defaultModel ?? effectivePreset.sampling.extra?.["model"] ?? "gpt-5.5";
  const tokenCounter = await getExactTokenCounter(String(model), effectivePreset.sampling.tokenizer);

  // 컨텍스트 조립용 최근 히스토리만 로드(결정 #9). 가상 스크롤 렌더와 별개이며,
  // buildPrompt 트림이 어차피 예산 밖을 버리므로 전체 로드와 결과가 같다.
  const existing = await readRecentMessages(chatId, effectivePreset.sampling.contextSize, tokenCounter);

  // 1) 사용자 메시지 즉시 커밋(결정 #9). 정규식 user-input → JS on-user-send 순으로
  //    변형(결정 #21/#17/#4).
  const order = nextOrder(existing);
  let storedUserText = regexScripts.length
    ? applyRegexScripts(userText, regexScripts, "user-input")
    : userText;
  storedUserText = await runScriptsForHook(
    chat,
    allScripts,
    "on-user-send",
    storedUserText,
    existing,
  );
  const userMessage = createMessage({
    chatId,
    senderType: "user",
    content: storedUserText,
    order,
    ...(persona ? { personaId: persona.id } : {}),
    ...(persona ? { senderName: persona.name } : {}),
  });
  await storage.putMessage(userMessage);

  // 2) 프롬프트 조립(결정 #12). 방금 보낸 사용자 메시지를 포함. 구조화 게임이면
  //    모듈의 턴 프로토콜 지시문을 시스템에 주입한다(결정 #25/#26). 트림은 위에서 만든
  //    정확 토크나이저(tiktoken)로 계산해 컨텍스트 한도 추정을 정밀화한다.
  const moduleInstructions = moduleInstructionsFor(module, chat);
  const promptMessages = [...existing, userMessage];
  // 로어북 스캔은 Web Worker로 오프로딩(결정 #9/#12). 결과 주입 시 buildPrompt가 내부
  // 스캔을 건너뛴다. scanText는 같은 messages로 만들어 등가성을 보장.
  const activation = await resolveActivation(
    lorebooks,
    promptMessages,
    character,
    persona,
    chatId,
  );
  const req = buildPrompt({
    preset: effectivePreset,
    character,
    chat,
    messages: promptMessages,
    model: String(model),
    stream: !shouldForceNonStreaming(effectivePreset),
    tokenCounter,
    ...(persona ? { persona } : {}),
    ...(lorebooks.length ? { lorebooks } : {}),
    ...(activation ? { activation } : {}),
    ...(moduleInstructions ? { moduleInstructions } : {}),
    ...(signal ? { signal } : {}),
  });

  callbacks.onStart?.();

  // 3) 생성. 스트리밍/논스트리밍 모두 텍스트를 누적해 마지막에 1회 커밋.
  const settings = await storage.getSettings();
  const forceNonStreaming = settings?.performance.forceNonStreaming ?? false;

  let fullText = "";
  let reasoning = "";
  let usage: CompletionUsage | undefined;

  if (forceNonStreaming || !req.stream) {
    const result = await aiGateway.complete({ ...req, stream: false }, profile);
    fullText = result.text;
    reasoning = result.reasoning ?? "";
    usage = result.usage;
    callbacks.onDelta?.(fullText);
  } else {
    for await (const chunk of aiGateway.stream(req, profile)) {
      if (signal?.aborted) break;
      usage = mergeUsage(usage, chunk.usage);
      if (chunk.delta) {
        fullText += chunk.delta;
        callbacks.onDelta?.(chunk.delta);
      }
      if (chunk.reasoningDelta) {
        reasoning += chunk.reasoningDelta;
        callbacks.onReasoning?.(chunk.reasoningDelta);
      }
    }
  }

  // 4) 구조화 게임 턴 처리(결정 #26/#28): 태그를 분해해 상태를 갱신하고, 화면/저장용
  //    본문은 narration으로 한다. 자유대화 모듈은 fullText를 그대로 쓴다. 주사위 시드는
  //    메시지 order 기반으로 재현 가능(결정 #4).
  const turn = processGameTurn(module, chat, fullText, order + 1);

  // 5) 어시스턴트 메시지 1회 커밋(결정 #14). 정규식 ai-output → JS after-response
  //    순으로 변형(결정 #21/#17/#4). 게임 모듈이면 narration을 베이스로 변형한다.
  let storedAiText = regexScripts.length
    ? applyRegexScripts(turn.narration, regexScripts, "ai-output")
    : turn.narration;
  storedAiText = await runScriptsForHook(
    chat,
    allScripts,
    "after-response",
    storedAiText,
    existing,
  );
  const assistantMessage = createMessage({
    chatId,
    senderType: "character",
    content: storedAiText,
    order: order + 1,
    characterId: character.id,
    senderName: character.name,
  });
  const variant = assistantMessage.variants[0];
  if (variant) {
    variant.model = String(model);
    if (reasoning) variant.reasoning = reasoning;
    variant.tokens = buildTokenLedger(req, fullText, reasoning, tokenCounter, profile, usage);
    if (turn.debug) {
      variant.extensions = {
        ...(variant.extensions ?? {}),
        [MODULE_TURN_DEBUG]: turn.debug,
      };
    }
  }
  // 게임 모듈 세션이면 이 턴 적용 전 상태를 메시지에 기록한다(결정 #16 재생성 롤백).
  if (module?.applyTurn) {
    assistantMessage.extensions = {
      ...(assistantMessage.extensions ?? {}),
      [MODULE_STATE_BEFORE]: moduleStateBefore,
      ...(turn.nextState ? { [MODULE_STATE_AFTER]: turn.nextState } : {}),
      ...(turn.debug ? { [MODULE_TURN_PARSED]: turn.debug.parsed, [MODULE_TURN_DIFF]: turn.debug.diff } : {}),
      ...(turn.diagnostics?.length ? { [MODULE_TURN_DIAGNOSTICS]: turn.diagnostics } : {}),
    };
  }
  await storage.putMessage(assistantMessage);

  // 채팅 메타 갱신(목록 정렬/카운트용, 결정 #9) + 모듈 상태 영속(결정 #28).
  await storage.chats.put({
    ...chat,
    lastMessageAt: Date.now(),
    messageCount: (chat.messageCount ?? existing.length) + 2,
    ...(turn.nextState ? { moduleState: turn.nextState } : {}),
  });

  return { userMessage, assistantMessage };
}

/** 프리셋 자체가 텍스트 컴플리션이거나 별도 플래그면 스트리밍을 끈다. */
function shouldForceNonStreaming(_preset: Preset): boolean {
  return false;
}

function mergeUsage(
  current: CompletionUsage | undefined,
  next: CompletionUsage | undefined,
): CompletionUsage | undefined {
  if (!next) return current;
  return {
    promptTokens: next.promptTokens ?? current?.promptTokens,
    completionTokens: next.completionTokens ?? current?.completionTokens,
    reasoningTokens: next.reasoningTokens ?? current?.reasoningTokens,
    totalTokens: next.totalTokens ?? current?.totalTokens,
  };
}

function buildTokenLedger(
  req: CompletionRequest,
  outputText: string,
  reasoningText: string,
  tokenCounter: TokenCounter,
  profile: ProviderProfile,
  usage: CompletionUsage | undefined,
): NonNullable<Message["variants"][number]["tokens"]> {
  const estimated = !usage;
  const prompt = usage?.promptTokens ?? estimateRequestTokens(req, tokenCounter);
  const completion = usage?.completionTokens ?? tokenCounter(outputText);
  const reasoning = usage?.reasoningTokens ?? (reasoningText ? tokenCounter(reasoningText) : undefined);
  const total = usage?.totalTokens ?? prompt + completion + (reasoning ?? 0);
  const cost = estimateTokenCost(
    { promptTokens: prompt, completionTokens: completion, reasoningTokens: reasoning },
    resolvePricing(req.model, req.params, profile.kind),
  );
  return {
    providerKind: profile.kind,
    ...(profile.providerId ? { providerId: profile.providerId } : {}),
    ...(profile.name ? { providerName: profile.name } : {}),
    prompt,
    completion,
    ...(reasoning ? { reasoning } : {}),
    total,
    estimated,
    ...(cost
      ? {
          inputCostUsd: cost.inputCostUsd,
          outputCostUsd: cost.outputCostUsd,
          reasoningCostUsd: cost.reasoningCostUsd,
          totalCostUsd: cost.totalCostUsd,
          pricingSource: cost.pricingSource,
        }
      : {}),
  };
}

/**
 * 마지막 어시스턴트 메시지를 재생성(스와이프, 결정 #16). 새 variant를 추가하고
 * activeVariant를 그쪽으로 옮긴다. 기존 구현은 단순화를 위해 마지막 사용자
 * 메시지를 다시 보내 새 응답을 받는 방식.
 */
export async function regenerateLast(
  chatId: string,
  callbacks: SendCallbacks = {},
  signal?: AbortSignal,
): Promise<Message | undefined> {
  const chat = await storage.chats.get(chatId);
  if (!chat) throw new Error("대화를 찾을 수 없습니다.");

  const [character, persona, preset, profile] = await Promise.all([
    resolveCharacter(chat),
    resolvePersona(chat),
    resolvePreset(chat),
    resolveProfile(chat),
  ]);

  // 모델·토크나이저를 먼저 정하고(히스토리 로드와 측정 일관성), 컨텍스트 조립용 최근
  // 히스토리만 로드한다(결정 #9). 마지막 어시스턴트 메시지는 항상 최근 창에 있다.
  const effectivePreset = withProviderSamplingDefaults(preset, profile);
  const model = profile.defaultModel ?? "gpt-5.5";
  const tokenCounter = await getExactTokenCounter(String(model), effectivePreset.sampling.tokenizer);
  const messages = await readRecentMessages(chatId, effectivePreset.sampling.contextSize, tokenCounter);

  const lastAssistant = [...messages].reverse().find((m) => m.senderType === "character");
  if (!lastAssistant) return undefined;

  const lorebooks = await resolveLorebooks(chat, character);
  const allScripts = await resolveAllScripts(character);
  const regexScripts = allScripts.filter((s) => s.language === "regex");
  const module = resolveModule(chat);

  // 게임 모듈 세션은 재생성 전에 상태를 "이 턴 적용 전"으로 되돌린다(결정 #16/#28).
  // 그래야 같은 사용자 입력에 대한 새 응답이 깨끗하게 다시 적용된다(상태 중첩 방지).
  const before = lastAssistant.extensions?.[MODULE_STATE_BEFORE] as
    | Record<string, unknown>
    | undefined;
  const chatForTurn: Chat =
    module?.applyTurn && before ? { ...chat, moduleState: before } : chat;

  // 재생성 대상 직전까지의 히스토리로 프롬프트 조립. 트림은 정확 토크나이저로(결정 #12).
  const upto = messages.filter((m) => m.order < lastAssistant.order);
  const moduleInstructions = moduleInstructionsFor(module, chatForTurn);
  // 로어북 스캔 오프로딩(결정 #9/#12). 같은 messages(upto)로 등가 보장.
  const activation = await resolveActivation(lorebooks, upto, character, persona, chatId);
  const req = buildPrompt({
    preset: effectivePreset,
    character,
    chat: chatForTurn,
    messages: upto,
    model: String(model),
    stream: true,
    tokenCounter,
    ...(persona ? { persona } : {}),
    ...(lorebooks.length ? { lorebooks } : {}),
    ...(activation ? { activation } : {}),
    ...(moduleInstructions ? { moduleInstructions } : {}),
    ...(signal ? { signal } : {}),
  });

  callbacks.onStart?.();

  const settings = await storage.getSettings();
  let fullText = "";
  let reasoning = "";
  let usage: CompletionUsage | undefined;
  if (settings?.performance.forceNonStreaming) {
    const result = await aiGateway.complete({ ...req, stream: false }, profile);
    fullText = result.text;
    reasoning = result.reasoning ?? "";
    usage = result.usage;
    callbacks.onDelta?.(fullText);
  } else {
    for await (const chunk of aiGateway.stream(req, profile)) {
      if (signal?.aborted) break;
      usage = mergeUsage(usage, chunk.usage);
      if (chunk.delta) {
        fullText += chunk.delta;
        callbacks.onDelta?.(chunk.delta);
      }
      if (chunk.reasoningDelta) {
        reasoning += chunk.reasoningDelta;
        callbacks.onReasoning?.(chunk.reasoningDelta);
      }
    }
  }

  // 구조화 게임 턴 처리(결정 #26/#28): 되돌린 상태 기준으로 새 응답을 적용.
  const turn = processGameTurn(module, chatForTurn, fullText, lastAssistant.order);

  // ai-output 정규식 → JS after-response 순으로 변형(sendUserMessage와 동일).
  let storedAiText = regexScripts.length
    ? applyRegexScripts(turn.narration, regexScripts, "ai-output")
    : turn.narration;
  storedAiText = await runScriptsForHook(chat, allScripts, "after-response", storedAiText, upto);

  // 새 variant 추가 + 활성화(결정 #16 스와이프).
  const next: Message = {
    ...lastAssistant,
    variants: [
      ...lastAssistant.variants,
      {
        content: storedAiText,
        createdAt: Date.now(),
        model: String(model),
        tokens: buildTokenLedger(req, fullText, reasoning, tokenCounter, profile, usage),
        ...(reasoning ? { reasoning } : {}),
        ...(turn.debug ? { extensions: { [MODULE_TURN_DEBUG]: turn.debug } } : {}),
      },
    ],
    activeVariant: lastAssistant.variants.length,
  };
  if (module?.applyTurn) {
    const previousExtensions = (next.extensions ?? {}) as Record<string, unknown>;
    const {
      [MODULE_STATE_AFTER]: _oldAfter,
      [MODULE_TURN_PARSED]: _oldParsed,
      [MODULE_TURN_DIFF]: _oldDiff,
      [MODULE_TURN_DIAGNOSTICS]: _oldDiagnostics,
      ...restExtensions
    } = previousExtensions;
    void _oldAfter;
    void _oldParsed;
    void _oldDiff;
    void _oldDiagnostics;
    next.extensions = {
      ...restExtensions,
      ...(turn.nextState ? { [MODULE_STATE_AFTER]: turn.nextState } : {}),
      ...(turn.debug ? { [MODULE_TURN_PARSED]: turn.debug.parsed, [MODULE_TURN_DIFF]: turn.debug.diff } : {}),
      ...(turn.diagnostics?.length ? { [MODULE_TURN_DIAGNOSTICS]: turn.diagnostics } : {}),
    };
  }
  await storage.putMessage(next);

  // 게임 모듈 세션이면 갱신된 상태를 영속(결정 #28). 자유대화는 상태 변화 없음.
  if (turn.nextState) {
    await storage.chats.put({ ...chat, moduleState: turn.nextState });
  }
  return next;
}
